import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PreviewFormat, PREVIEW_FORMATS } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';
import { useWorkspaces } from '../../providers/WorkspaceProvider';
import { useQuickGenPreset } from '../hooks/useQuickGenPreset';
import { useQuickGenerate } from '../hooks/useQuickGenerate';
import {
  Container, H1, Lead, FieldGroup, Label, Input,
  OptionGrid, OptionButton, OptionIcon, OptionHint,
  PrimaryButton, ErrorBox, PromptArea, CharCount, FieldHint, CheckRow,
  StatusBox, StatusText, StatusHint, ProgressBar, ProgressFill,
} from '../styled';

type Model = 'ai' | 'stock' | 'asset';

interface StyleOption {
  id: string;
  label: string;
  preview: string;
  custom: boolean;
}

const FORMAT_OPTIONS: { id: PreviewFormat; icon: string; hint: string }[] = [
  { id: 'square',     icon: '⬛', hint: '1:1' },
  { id: 'portrait',   icon: '▮',  hint: '4:5' },
  { id: 'vertical',   icon: '📱', hint: '9:16' },
  { id: 'widescreen', icon: '▭',  hint: '16:9' },
];

// Tope de los dos campos de prompt. Espeja USER_IMAGE_PROMPT_MAX y
// AI_TRANSFER_INSTRUCTIONS_MAX del server (2000, subido desde 300 el
// 2026-09-09). El server recorta igual: esto es para que la persona VEA que se
// está acercando, no para reemplazar la validación de allá.
const PROMPT_MAX = 2000;

const MODEL_OPTIONS: { id: Model; label: string; icon: string; hint: string }[] = [
  { id: 'ai',    label: 'AI',     icon: '🤖', hint: 'AI-generated' },
  { id: 'stock', label: 'Stock',  icon: '📷', hint: 'Stock photos' },
  { id: 'asset', label: 'Custom', icon: '🖼️', hint: 'Your own images' },
];

// Idiomas para el contenido de los ads (no para la UI). Mismas opciones que
// NovahGenerateChat ofrece en el flow legacy.
const LANGUAGE_OPTIONS: { id: string; label: string; flag: string }[] = [
  { id: 'English',    label: 'English',    flag: '🇺🇸' },
  { id: 'Spanish',    label: 'Español',    flag: '🇪🇸' },
  { id: 'Portuguese', label: 'Português',  flag: '🇧🇷' },
  { id: 'French',     label: 'Français',   flag: '🇫🇷' },
  { id: 'Italian',    label: 'Italiano',   flag: '🇮🇹' },
  { id: 'German',     label: 'Deutsch',    flag: '🇩🇪' },
];

/**
 * Quick Gen wizard: the single step to generate ads from a URL.
 * Inputs: brand URL, format, image model, ads language.
 * If the user already has a saved preset, the radios come pre-selected.
 */
export function WizardPage() {
  const navigate = useNavigate();
  const { preset, loaded: presetLoaded } = useQuickGenPreset();
  const { phase, phaseMessage, progress, error, run } = useQuickGenerate();
  const { workspaces } = useWorkspaces();

  // Origen del brand: pegar una URL nueva, o reusar un workspace que ya tengas.
  const [sourceMode, setSourceMode] = useState<'url' | 'workspace'>('url');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [url, setUrl] = useState('');
  const [format, setFormat] = useState<PreviewFormat>('square');
  const [model, setModel] = useState<Model>('ai');
  const [language, setLanguage] = useState<string>('English');
  // Cantidad de ads a generar. Cap 1-10 (10 = ~$0.40 con baked Nano Banana).
  const [count, setCount] = useState<number>(4);
  // Estilos "baked AI poster" elegidos. Si hay >=1, el 100% de los ads se
  // generan como poster full IA (texto baked dentro, sin template) haciendo
  // round-robin entre los estilos elegidos. Si esta vacio, va por `model`.
  const [styles, setStyles] = useState<StyleOption[]>([]);
  const [selectedStyleIds, setSelectedStyleIds] = useState<string[]>([]);
  const [useLogo, setUseLogo] = useState(false);
  // Los dos prompts que escribe la persona. Ver PROMPT_MAX abajo y
  // server/services/generation/userImagePrompt.ts para el porqué de que sean dos.
  const [imagePrompt, setImagePrompt] = useState('');
  const [imagePromptExact, setImagePromptExact] = useState(false);
  const [copyPrompt, setCopyPrompt] = useState('');

  // Apply saved preset once loaded.
  useEffect(() => {
    if (!presetLoaded || !preset) return;
    if (preset.format) setFormat(preset.format);
    if (preset.model) setModel(preset.model);
    if (preset.language) setLanguage(preset.language);
  }, [presetLoaded, preset]);

  // Cargar los estilos disponibles para "AI-styled posters" (built-in + custom).
  useEffect(() => {
    apiClient.get<{ styles: StyleOption[] }>('/api/quickgen/styles')
      .then((data) => setStyles(data.styles))
      .catch(() => { /* silencioso: el selector simplemente queda vacio */ });
  }, []);

  const toggleStyle = (id: string) => {
    setSelectedStyleIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const isBusy = phase === 'preparing' || phase === 'generating';

  const usingWorkspace = sourceMode === 'workspace';
  const selectedWorkspace = workspaces.find((w) => w.id === selectedWorkspaceId);
  const canSubmit = usingWorkspace ? !!selectedWorkspaceId : !!url.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || isBusy) return;
    const result = await run({
      url: usingWorkspace ? undefined : url.trim(),
      workspaceId: usingWorkspace ? selectedWorkspaceId : undefined,
      workspaceName: usingWorkspace ? selectedWorkspace?.name : undefined,
      format, model, language,
      count,
      styleIds: selectedStyleIds,
      useLogo,
      imagePrompt: imagePrompt.trim(),
      imagePromptExact,
      copyPrompt: copyPrompt.trim(),
    });
    if (result) {
      // Pass posts via location.state to avoid a refetch on the preview.
      // url is forwarded so the preview can derive a default contact text.
      navigate(`/quick-gen/preview/${result.workspaceId}`, {
        state: { posts: result.posts, format, model, language, url: usingWorkspace ? '' : url.trim() },
      });
    }
  };

  return (
    <>
      <Container $narrow>
        {phase === 'idle' || phase === 'error' ? (
          <form onSubmit={handleSubmit}>
            <H1>Generate ads from a URL</H1>
            <Lead>
              Paste your brand’s website, pick a format and an image model.
              In seconds you’ll have ads ready to publish.
            </Lead>

            <FieldGroup>
              <Label>Brand</Label>
              <OptionGrid $cols={2}>
                <OptionButton type="button" $selected={sourceMode === 'url'} onClick={() => setSourceMode('url')} disabled={isBusy}>
                  <OptionIcon>🌐</OptionIcon>
                  <span>From a URL</span>
                  <OptionHint>Scrape a website</OptionHint>
                </OptionButton>
                <OptionButton type="button" $selected={sourceMode === 'workspace'} onClick={() => setSourceMode('workspace')} disabled={isBusy || workspaces.length === 0}>
                  <OptionIcon>🗂️</OptionIcon>
                  <span>Existing workspace</span>
                  <OptionHint>{workspaces.length === 0 ? 'None yet' : 'Reuse your brand'}</OptionHint>
                </OptionButton>
              </OptionGrid>

              {sourceMode === 'url' ? (
                <Input
                  id="url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://your-brand.com"
                  autoFocus
                  disabled={isBusy}
                  style={{ marginTop: 12 }}
                />
              ) : (
                <select
                  value={selectedWorkspaceId}
                  onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                  disabled={isBusy}
                  style={{
                    width: '100%', marginTop: 12, padding: '14px 16px', borderRadius: 10,
                    border: '1px solid #E5E7EB', fontSize: 15, fontFamily: 'inherit',
                    background: 'white', cursor: isBusy ? 'not-allowed' : 'pointer',
                  }}
                >
                  <option value="">Choose a workspace…</option>
                  {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              )}
            </FieldGroup>

            <FieldGroup>
              <Label>Format</Label>
              <OptionGrid $cols={4}>
                {FORMAT_OPTIONS.map((opt) => (
                  <OptionButton
                    key={opt.id}
                    type="button"
                    $selected={format === opt.id}
                    onClick={() => setFormat(opt.id)}
                  >
                    <OptionIcon>{opt.icon}</OptionIcon>
                    <span>{PREVIEW_FORMATS[opt.id].label.split(' ')[0]}</span>
                    <OptionHint>{opt.hint}</OptionHint>
                  </OptionButton>
                ))}
              </OptionGrid>
            </FieldGroup>

            <FieldGroup>
              <Label>Image type</Label>
              <OptionGrid $cols={3}>
                {MODEL_OPTIONS.map((opt) => (
                  <OptionButton
                    key={opt.id}
                    type="button"
                    $selected={model === opt.id}
                    onClick={() => setModel(opt.id)}
                  >
                    <OptionIcon>{opt.icon}</OptionIcon>
                    <span>{opt.label}</span>
                    <OptionHint>{opt.hint}</OptionHint>
                  </OptionButton>
                ))}
              </OptionGrid>
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="qg-image-prompt">Describe your image <OptionHint>(optional)</OptionHint></Label>
              <PromptArea
                id="qg-image-prompt"
                value={imagePrompt}
                maxLength={PROMPT_MAX}
                disabled={isBusy}
                placeholder={'PROFESSIONAL ELECTRIC SERVICES\nBelow: Central Nashville\nSubheading: Safe. Reliable. Affordable.\nAdd a clean service list with simple matching icons...'}
                onChange={(e) => setImagePrompt(e.target.value)}
              />
              <CharCount $near={imagePrompt.length > PROMPT_MAX * 0.9}>
                {imagePrompt.length} / {PROMPT_MAX}
              </CharCount>
              <FieldHint>
                Write what the image should show — subject, layout, any text on it.
                Leave it empty and the AI will decide for you.
              </FieldHint>
              <CheckRow>
                <input
                  type="checkbox"
                  checked={imagePromptExact}
                  disabled={isBusy || !imagePrompt.trim()}
                  onChange={(e) => setImagePromptExact(e.target.checked)}
                />
                <span>
                  Use my description exactly — send it to the image model word for word.
                  Your brand name and contact details will <strong>not</strong> be added automatically.
                </span>
              </CheckRow>
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="qg-copy-prompt">What should the copy say? <OptionHint>(optional)</OptionHint></Label>
              <PromptArea
                id="qg-copy-prompt"
                value={copyPrompt}
                maxLength={PROMPT_MAX}
                disabled={isBusy}
                placeholder="Storm damage — we can help"
                onChange={(e) => setCopyPrompt(e.target.value)}
              />
              <CharCount $near={copyPrompt.length > PROMPT_MAX * 0.9}>
                {copyPrompt.length} / {PROMPT_MAX}
              </CharCount>
              <FieldHint>
                This is the topic of the written post — separate from the image above.
              </FieldHint>
            </FieldGroup>

            <FieldGroup>
              <Label>Ads language</Label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={isBusy}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: 10,
                  border: '1px solid #E5E7EB',
                  fontSize: 15,
                  fontFamily: 'inherit',
                  background: 'white',
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                }}
              >
                {LANGUAGE_OPTIONS.map(o => (
                  <option key={o.id} value={o.id}>{o.flag} {o.label}</option>
                ))}
              </select>
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="count">How many ads? ({count})</Label>
              <input
                id="count"
                type="range"
                min={1}
                max={10}
                step={1}
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value, 10))}
                disabled={isBusy}
                style={{ width: '100%' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                <span>1</span><span>10</span>
              </div>
            </FieldGroup>

            <FieldGroup>
              <Label>AI-styled posters</Label>
              <p style={{ fontSize: 12, color: '#6B7280', marginTop: -4, marginBottom: 10 }}>
                Pick one or more styles. Every ad comes back as full AI artwork
                (text baked in, no template), cycling through your picks. Leave
                empty to use your normal templates.
              </p>
              {styles.length > 0 ? (
                <OptionGrid $cols={3}>
                  {styles.map((s) => {
                    const selected = selectedStyleIds.includes(s.id);
                    return (
                      <OptionButton
                        key={s.id}
                        type="button"
                        $selected={selected}
                        onClick={() => toggleStyle(s.id)}
                        disabled={isBusy}
                        style={{ padding: 0, overflow: 'hidden', position: 'relative' }}
                      >
                        {s.preview ? (
                          <img
                            src={s.preview}
                            alt={s.label}
                            loading="lazy"
                            style={{ width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', display: 'block' }}
                          />
                        ) : (
                          <div style={{ width: '100%', aspectRatio: '3 / 4', background: '#F1F5F9' }} />
                        )}
                        <span style={{ display: 'block', padding: '8px 6px', fontSize: 13, fontWeight: 600 }}>
                          {s.label}
                        </span>
                        {selected && (
                          <span style={{
                            position: 'absolute', top: 6, right: 6, width: 22, height: 22,
                            borderRadius: 999, background: '#2563EB', color: '#fff', fontSize: 13,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>✓</span>
                        )}
                        {s.custom && (
                          <span style={{
                            position: 'absolute', top: 6, left: 6, padding: '1px 6px', borderRadius: 999,
                            background: '#E0E7FF', color: '#3730A3', fontSize: 10, fontWeight: 700,
                          }}>Custom</span>
                        )}
                      </OptionButton>
                    );
                  })}
                </OptionGrid>
              ) : (
                <p style={{ fontSize: 13, color: '#9CA3AF' }}>Loading styles…</p>
              )}
              {selectedStyleIds.length > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={useLogo} onChange={(e) => setUseLogo(e.target.checked)} />
                  <span><strong>Include my logo</strong> — integrate the real brand logo into the poster</span>
                </label>
              )}
            </FieldGroup>

            <PrimaryButton type="submit" disabled={!canSubmit || isBusy}>
              Generate ads
            </PrimaryButton>

            {error && <ErrorBox>{error}</ErrorBox>}
          </form>
        ) : (
          <StatusBox>
            <StatusText>{phaseMessage}</StatusText>
            <StatusHint>
              {phase === 'preparing'
                ? 'Analyzing your website to understand the brand'
                : `${progress.current}/${progress.total} ads generated`}
            </StatusHint>
            {phase === 'generating' && progress.total > 0 && (
              <ProgressBar>
                <ProgressFill $pct={(progress.current / progress.total) * 100} />
              </ProgressBar>
            )}
          </StatusBox>
        )}
      </Container>
    </>
  );
}
